import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'

// Components
import Text from './Text'
import Button, { ColorVariant } from './Button'
import { RowAligned } from './layout/LayoutAtoms'

// Styles
import { mqTo } from '../styles/mediaQueries'
import { shadows } from '../styles/effects'

// Types
import { IconType } from './icon/IconType'

interface IProps {
  onClick: () => void
  text?: string
  buttonText: string
  buttonIcon?: IconType
  disableButton?: boolean
  // Save button preset. Defaults to 'primary' (the global save-bar look).
  buttonColorVariant?: ColorVariant
  // When provided, FormFinish manages its own presence: it stays mounted through
  // the exit animation when `isOpen` flips to false, then unmounts. Omit it to
  // keep the legacy behaviour (caller mounts/unmounts, enter animation only).
  isOpen?: boolean
  children?: React.ReactNode
  className?: string
  // Cypress handle, forwarded to the action button.
  'data-cy'?: string
}

// Keep in sync with the exit animation duration below.
const EXIT_MS = 180

const FormFinish: React.FC<IProps> = ({
  text,
  buttonText,
  buttonIcon,
  disableButton,
  buttonColorVariant = 'primary',
  isOpen,
  onClick,
  children,
  className,
  'data-cy': dataCy,
}) => {
  const controlled = isOpen !== undefined
  const [mounted, setMounted] = useState(isOpen ?? true)
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    if (!controlled) return
    if (isOpen) {
      setExiting(false)
      setMounted(true)
      return
    }
    // Play the exit animation, then unmount once it has finished.
    setExiting(true)
    const timer = setTimeout(() => setMounted(false), EXIT_MS)
    return () => clearTimeout(timer)
  }, [isOpen])

  if (controlled && !mounted) return null

  return (
    <Container className={className} data-exiting={exiting || undefined}>
      <RowAligned>
        {text && (
          <Text textAlign="center" marginRight={24} color="text">
            {text}
          </Text>
        )}
        <SaveButton
          caption={buttonText}
          icon={buttonIcon}
          colorVariant={buttonColorVariant}
          onClick={onClick}
          disabled={disableButton}
          data-cy={dataCy}
        />
      </RowAligned>
      {children}
    </Container>
  )
}

export default FormFinish

// Enter: fade + rise from below, materialising into focus (a touch of blur).
const enter = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`

// Exit: subtler and quicker than the enter — a soft downward fade before unmount.
const exit = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
  to {
    opacity: 0;
    transform: translateY(10px);
    filter: blur(4px);
  }
`

const Container = styled.div`
  /* Solid white base + a translucent primary-15% tint, kept opaque so page
     content never shows through the fixed bar. */
  background-color: ${({ theme }) => theme.colors.lightest};
  background-image: linear-gradient(
    ${({ theme }) => theme.colors.primaryFadeLight},
    ${({ theme }) => theme.colors.primaryFadeLight}
  );
  position: fixed;
  bottom: 24px;
  left: 50%;
  /* Centre via the translate property (not transform) so the enter/exit
     keyframes are free to animate transform without fighting the -50% offset. */
  translate: -50% 0;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.primaryFadeHalf};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
  box-shadow: ${shadows.depthFive};
  /* Snappy ease-out so it pops up from below as it fades in. */
  animation: ${enter} 260ms cubic-bezier(0.16, 1, 0.3, 1) both;

  &[data-exiting='true'] {
    animation: ${exit} ${EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1) both;
  }

  ${mqTo.M`
    left: 0;
    width: 100%;
    translate: none;
    /* Full-width bar pinned to the bottom: only the top edge reads as a divider. */
    border-width: 1px 0 0 0;
    border-radius: 0;
    bottom: 0;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.01ms !important;
  }
`

const SaveButton = styled(Button)`
  ${mqTo.M`
    margin-left: auto;
  `}
`
